// Global tel:-link click listener. Every phone call button on any page in this
// project (main site + micro-sites) fires an email to support@obriencontractingusa.com
// with the number tapped, the visitor's known phone/name/email if we have them,
// the page, and session/visitor IDs.
//
// One-time install — safe to call from anywhere; a document-level capture
// listener catches clicks even from dynamically rendered CTAs.

const INSTALL_FLAG = "__obcPhoneClickNotifierInstalled";
const BUSINESS_PHONE_DIGITS = "12399194485";

function phoneDigits(value?: string | null): string {
  return (value || "").replace(/\D/g, "");
}

function isBusinessPhone(value?: string | null): boolean {
  const digits = phoneDigits(value);
  if (!digits) return false;
  const normalized = digits.length === 10 ? `1${digits}` : digits;
  return normalized === BUSINESS_PHONE_DIGITS;
}

function readCapturedContact(): { name?: string; email?: string; phone?: string } {
  try {
    const raw =
      localStorage.getItem("obc_autocapture_snapshot") ||
      localStorage.getItem("obc_autofill_snapshot");
    if (raw) {
      const snap = JSON.parse(raw);
      const phone = snap.phone || snap.tel || undefined;
      return {
        name: snap.name || snap.full_name || undefined,
        email: snap.email || undefined,
        phone: phone && !isBusinessPhone(phone) ? phone : undefined,
      };
    }
  } catch {
    /* ignore */
  }
  return {};
}

async function reportPhoneClick(phoneCalled: string, ctaLabel?: string | null) {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    if (!supabaseUrl || !supabaseAnonKey) return;

    const captured = readCapturedContact();
    const calledDigits = phoneDigits(phoneCalled);
    const normalizedCalled = calledDigits.length === 10 ? `1${calledDigits}` : calledDigits;
    const visitorDigits = phoneDigits(captured.phone);
    const normalizedVisitor = visitorDigits.length === 10 ? `1${visitorDigits}` : visitorDigits;
    const visitorPhone =
      normalizedVisitor && normalizedVisitor !== normalizedCalled && !isBusinessPhone(captured.phone)
        ? captured.phone
        : null;
    const payload = {
      phone_called: phoneCalled,
      visitor_phone: visitorPhone,
      visitor_name: captured.name || null,
      visitor_email: captured.email || null,
      visitor_id: localStorage.getItem("obc_visitor_id"),
      session_id: sessionStorage.getItem("obc_session_id"),
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      host: window.location.host,
      cta_label: ctaLabel || null,
      timestamp: new Date().toISOString(),
    };

    const body = JSON.stringify(payload);
    const url = `${supabaseUrl}/functions/v1/notify-phone-click`;

    // Prefer sendBeacon so navigation to the dialer doesn't cancel the request.
    // Use text/plain to avoid a cross-origin preflight; mobile dialer handoff can
    // cancel preflighted JSON beacons before the Edge Function ever receives them.
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }

    // Fallback fetch with keepalive.
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${supabaseAnonKey}`,
      },
      body,
      keepalive: true,
    });
  } catch {
    /* never break a phone call for tracking */
  }
}

export function installPhoneClickNotifier() {
  if (typeof window === "undefined") return;
  const w = window as any;
  if (w[INSTALL_FLAG]) return;
  w[INSTALL_FLAG] = true;
  let lastSignature = "";
  let lastSentAt = 0;

  document.addEventListener(
    "click",
    (evt) => {
      const target = evt.target as Element | null;
      if (!target) return;
      const anchor = (target.closest?.("a[href^='tel:']") ||
        target.closest?.("[data-phone-cta]")) as HTMLAnchorElement | HTMLElement | null;
      if (!anchor) return;

      let phone = "";
      if (anchor instanceof HTMLAnchorElement && anchor.href.startsWith("tel:")) {
        phone = anchor.href.replace(/^tel:/i, "").trim();
      } else {
        phone = anchor.getAttribute("data-phone") || "";
      }

      const label =
        anchor.getAttribute("data-track-cta") ||
        anchor.getAttribute("aria-label") ||
        (anchor.textContent || "").trim().slice(0, 80) ||
        "phone_click";

      const signature = `${phone}|${window.location.pathname}|${label}`;
      const now = Date.now();
      if (signature === lastSignature && now - lastSentAt < 2500) return;
      lastSignature = signature;
      lastSentAt = now;

      // Fire-and-forget, non-blocking.
      void reportPhoneClick(phone, label);
    },
    { capture: true },
  );
}

export const __testReportPhoneClick = reportPhoneClick;
